import { useEffect, useState } from "react";
import { Phone, Menu, X } from "lucide-react";
import { CLINIC } from "../data/clinic";
import { LOGO_DARK } from "../data/assets";
import { WhatsAppIcon } from "./SocialIcons";

const NAV = [
  { id: "services", label: "Services" },
  { id: "conditions", label: "Conditions" },
  { id: "areas", label: "Areas" },
  { id: "about", label: "About" },
  { id: "contact", label: "Book a Visit" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#1B0B3A]/85 backdrop-blur-xl border-b border-[#4A2D7A]/60"
          : "bg-[#1B0B3A]/40 backdrop-blur-sm"
      }`}
    >
      <div className="lfc-container flex items-center justify-between py-3.5">
        <button
          data-testid="logo-home"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 group"
        >
          <span className="grid place-items-center w-16 h-16 rounded-2xl bg-[#2D1456] ring-1 ring-[#2BB8B4]/40 overflow-hidden p-1.5">
            <img src={LOGO_DARK} alt="Litisha Foot Care" className="w-full h-full object-contain" />
          </span>
          <span className="hidden xl:flex flex-col text-left leading-tight">
            <span className="text-[15px] font-semibold tracking-tight text-white" style={{ fontFamily: "Outfit" }}>
              Litisha Foot Care
            </span>
            <span className="text-[10.5px] uppercase tracking-[0.22em] text-[#5FE6E0]">
              Mobile Clinic
            </span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-${n.id}`}
              onClick={() => goTo(n.id)}
              className="text-[14.5px] text-[#C2B4E6] hover:text-white transition-colors duration-200 font-medium"
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="header-phone"
            href={`tel:${CLINIC.phoneRaw}`}
            className="hidden md:inline-flex items-center gap-2 text-[14.5px] font-medium text-[#5FE6E0] hover:text-white transition"
          >
            <Phone className="w-4 h-4" strokeWidth={1.6} />
            {CLINIC.phone}
          </a>
          <a
            data-testid="header-whatsapp"
            href={CLINIC.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Message Litisha Foot Care on WhatsApp"
            className="hidden sm:grid place-items-center w-10 h-10 rounded-full bg-white hover:scale-105 hover:-translate-y-0.5 transition-all duration-300 shadow-sm shrink-0"
          >
            <WhatsAppIcon size={22} />
          </a>
          <button
            data-testid="header-book-cta"
            onClick={() => goTo("contact")}
            className="lfc-btn-primary text-[14px] hidden sm:inline-flex"
          >
            Book a Visit
          </button>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden grid place-items-center w-10 h-10 rounded-full border border-[#4A2D7A] text-white"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-[#4A2D7A] bg-[#1B0B3A]/95 backdrop-blur-xl">
          <div className="lfc-container py-6 flex flex-col gap-4">
            {NAV.map((n) => (
              <button
                key={n.id}
                data-testid={`mobile-nav-${n.id}`}
                onClick={() => goTo(n.id)}
                className="text-left text-base text-[#F5F2FF] py-2 border-b border-[#38216A]"
              >
                {n.label}
              </button>
            ))}
            <a
              href={`tel:${CLINIC.phoneRaw}`}
              className="lfc-btn-primary justify-center mt-2"
              data-testid="mobile-call-cta"
            >
              <Phone className="w-4 h-4" strokeWidth={1.6} />
              Call {CLINIC.phone}
            </a>
            <a
              href={CLINIC.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="lfc-btn-secondary justify-center"
              data-testid="mobile-whatsapp-cta"
            >
              <WhatsAppIcon size={18} />
              WhatsApp us
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
